import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { useSeo, breadcrumb } from "../lib/seo";
import { MapPin, Clock, Plane, ExternalLink, Phone } from "lucide-react";

const DETAILS = {
  "DXB-T1": { pickup: "Arrivals Hall, Terminal 1 — look for the green Europcar desk, 24/7" },
  "DXB-T3": { pickup: "Arrivals Hall, Terminal 3, beside the hotel desks — 24/7 counter" },
  "DXB-HQ": { pickup: "6th Street, Al Quoz Industrial Area 1 — near the Porsche showroom, Sheikh Zayed Road Exit 41" },
  "DXB-DHM": { pickup: "Dubai Hills Mall — Ground Floor / P1 parking level, Al Khail Road entrance" },
  "DXB-ET": { pickup: "Jumeirah Emirates Towers — hotel entrance, Trade Centre Second" },
  "SHJ-AP": { pickup: "Car Rental Center, Arrivals Hall — 24/7 desk" },
  "SHJ-CT": { pickup: "Abu Al Reesh Building (ground floor), Al Zahra Street, near King Faisal Mosque" },
  "RAK-HAM": { pickup: "Al Hamra Mall, Ground Floor — Al Hamra Village, Sheikh Mohamed Bin Salem Road" },
  "FUJ-CT": { pickup: "Al Awadhi Office Towers (FEWA Office Building), Hamad Bin Abdulla Road" },
};

export default function Locations() {
  const { t } = useTranslation();
  const [branches, setBranches] = useState(null);
  useSeo({
    title: "Car Rental Locations in Dubai & Northern Emirates | Europcar UAE",
    description: "14 Europcar branches across the UAE, including 24/7 airport desks at Dubai Terminals 1, 2 and 3 and Sharjah Airport. Addresses, opening hours and the 800 EUROPCAR line.",
    jsonLd: [breadcrumb([["Home", "/"], ["Locations", "/locations"]])],
  });

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const hours = (s) => {
    if (s.hours.close === 24) return t("Open 24/7");
    const base = `${s.hours.open}:00 – ${s.hours.close}:00`;
    return s.closed_days?.length ? t("Mon–Sat {{base}} · Sun closed", { base }) : t("Daily {{base}}", { base });
  };
  const mapUrl = (s) => `https://www.google.com/maps/search/${encodeURIComponent(`Europcar ${s.name} ${s.address}`)}`;

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 promo-stripe">
          <h1 className="heading-upper text-2xl sm:text-3xl text-white" data-testid="locations-heading">{t("Wherever you land, we're close.")}</h1>
          <p className="text-white/80 text-sm mt-2 max-w-2xl">{t("14 branches across the UAE, with airport desks open around the clock and doorstep delivery reaching all six emirates.")}</p>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full flex-1 space-y-10">
        {branches?.groups.map((g) => (
          <section key={g.emirate} data-testid={`locations-group-${g.emirate.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="promo-stripe mb-4">
              <h2 className="heading-upper text-xl text-[#144733]">{t(g.emirate)}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {g.stations.map((s) => {
                const d = DETAILS[s.id] || {};
                return (
                  <div key={s.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-5 card-lift flex flex-col" data-testid={`location-card-${s.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-sm text-[#0F1E17]">{s.name}</h3>
                      {s.type === "airport" && <span className="bg-[#144733] text-white text-[9px] uppercase tracking-widest px-2 py-1 rounded-full flex items-center gap-1"><Plane className="w-3 h-3" />{t("Airport")}</span>}
                    </div>
                    <p className="text-xs text-[#57685e] mt-2 flex gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#009900] shrink-0" />{s.address}</p>
                    <p className="text-xs text-[#57685e] mt-1 flex gap-1.5"><Clock className="w-3.5 h-3.5 text-[#009900] shrink-0" />{hours(s)}</p>
                    {s.phone && <p className="text-xs text-[#57685e] mt-1 flex gap-1.5" data-testid={`location-phone-${s.id}`}><Phone className="w-3.5 h-3.5 text-[#009900] shrink-0" />{s.phone}</p>}
                    {d.pickup && <p className="text-[11px] text-[#41504a] mt-2 bg-[#fafcfa] border border-dashed border-[#e2ebe4] rounded p-2">{t("Pickup: {{d}}", { d: t(d.pickup) })}</p>}
                    <div className="mt-auto pt-4 flex gap-2">
                      <Link to={`/?branch=${s.id}#book`} className="btn-cta rounded px-4 py-2 text-[11px] flex-1 text-center" data-testid={`location-reserve-${s.id}`}>{t("Reserve here")}</Link>
                      <a href={mapUrl(s)} target="_blank" rel="noreferrer" className="border border-[#d5ddd5] rounded px-3 py-2 text-[11px] font-semibold text-[#144733] hover:border-[#009900] flex items-center gap-1" data-testid={`location-map-${s.id}`}>{t("Map")} <ExternalLink className="w-3 h-3" /></a>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        <div className="bg-[#f0f9f0] border border-[#009900]/20 rounded-2xl p-6 text-sm text-[#144733]" data-testid="locations-delivery-note">
          {t("Prefer not to travel? We deliver to your door across all six emirates, daily 09:00–21:00 — in as little as 4 hours in Dubai & Sharjah.")} <Link to="/#book" className="underline font-semibold">{t("Book a delivery")}</Link>. {t("Note: Abu Dhabi & Al Ain are served by Europcar Abu Dhabi.")}
        </div>
      </main>
      <Footer />
    </div>
  );
}
